//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import moment, { Moment } from 'moment-timezone';
import { List } from 'ts-toolbelt';

import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';

export function momentFromDate(d: Date | number, timeZone: TimeZoneId): Moment {
  return moment.tz(d, timeZone);
}

export function isAMoment(obj: unknown): obj is Moment {
  return !!obj && !!(obj as { _isAMomentObject?: boolean })._isAMomentObject;
}

/**
 * Get the day of week of a moment, in 3 letter lowercase abbreviated form.
 * @param mo Moment object
 */
export function getDayOfWeek(mo: Moment): WeekDayAbbr {
  return mo.format('ddd') as WeekDayAbbr;
}

export const DAYS_OF_WEEK_ABBR = [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
] as const;
export type WeekDayAbbr = List.UnionOf<typeof DAYS_OF_WEEK_ABBR>;
export const DAYS_OF_WEEK_VALUES = [0, 1, 2, 3, 4, 5, 6] as const;
export type WeekDayValue = List.UnionOf<typeof DAYS_OF_WEEK_VALUES>;

export const DAYS_OF_WEEK = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;
export type WeekDay = typeof DAYS_OF_WEEK[number];

const TWENTY_FOUR_HOURS = 24;

export const HOURS_IN_DAY = Array.from(
  { length: TWENTY_FOUR_HOURS },
  (_, k) => k,
);

/**
 * Hours in a day from 0 to 23
 * @example hoursInDay[0] = {hour: 0, formattedHour: '12 AM'}
 */
export const hoursInDayWithFormattedHour = HOURS_IN_DAY.map((hour) => ({
  hour,
  formattedHour: moment(hour, 'H').format('h A'),
}));

/**
 *
 * @returns The momement with its year, month, and date set to match the given date string
 */
export const momentWithDate = (moment: Moment, dateString: DateString) => {
  const { year, month, day } = DateString.parse(dateString);

  return moment
    .year(year)
    .month(month - 1) // DateString is 1 indexed but moment is 0 indexed
    .date(day);
};
